import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useFrontendAuth } from '../hooks/useAuth';
import { getLoginPromptQueue, markLoginPromptClosed } from '../services/api.shop';
import { isCookieConsentPending } from '../components/tour/tourDom';
import type { ShopUserInfo } from '../types/auth';
import PendingMeasurementPrompt from '../components/PendingMeasurementPrompt';
import PendingMyPetitoNudge from '../components/PendingMyPetitoNudge';
import PendingRatingPrompt from '../components/PendingRatingPrompt';
import { LoginPromptContext, type LoginPromptApi } from './loginPromptContext';
import { LOGIN_PROMPTS, isLoginPromptId, type LoginPromptId } from './registry';
import { isLoginPromptQuietPath, mergeLoginPromptOrder, pickLoginPrompt } from './pickLoginPrompt';

/**
 * One slot for on-login prompts. Each prompt keeps its own layout and only renders while it holds the slot.
 * The gap timer is armed in settle/stampClosed, never while slotOpen is true.
 */
export function LoginPromptProvider({ children }: { children: ReactNode }) {
    const { isAuthenticated, isAuthReady, user } = useFrontendAuth();
    const { pathname } = useLocation();
    const queryClient = useQueryClient();

    const [grantedId, setGrantedId] = useState<LoginPromptId | null>(null);
    const [claimed, setClaimed] = useState(false);
    const [interruptId, setInterruptId] = useState<LoginPromptId | null>(null);
    const [holdCount, setHoldCount] = useState(0);
    const [waitingVersion, setWaitingVersion] = useState(0);
    const [tick, setTick] = useState(0);
    const [cookieReady, setCookieReady] = useState(() => !isCookieConsentPending());

    const grantedIdRef = useRef<LoginPromptId | null>(null);
    const interruptRef = useRef<LoginPromptId | null>(null);
    const waitingRef = useRef<Partial<Record<LoginPromptId, boolean>>>({});
    const holdsRef = useRef(new Set<string>());
    grantedIdRef.current = grantedId;
    interruptRef.current = interruptId;

    const queueQuery = useQuery({
        queryKey: ['loginPromptQueue'],
        queryFn: getLoginPromptQueue,
        staleTime: 60_000,
        retry: 1,
    });

    useEffect(() => {
        if (cookieReady) return;
        const id = window.setInterval(() => {
            if (!isCookieConsentPending()) setCookieReady(true);
        }, 400);
        return () => window.clearInterval(id);
    }, [cookieReady]);

    const ordered = useMemo(
        () => mergeLoginPromptOrder(LOGIN_PROMPTS, queueQuery.data?.prompts ?? []),
        [queueQuery.data]
    );

    const waitingIds = useMemo(() => {
        return LOGIN_PROMPTS.filter((prompt) => waitingRef.current[prompt.id]).map((prompt) => prompt.id);
        // waitingVersion is the signal that waitingRef changed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [waitingVersion]);

    const stamp = useCallback(async () => {
        const optimistic = new Date().toISOString();
        queryClient.setQueryData<ShopUserInfo | null>(['user'], (prev) =>
            prev ? { ...prev, lastLoginPromptClosedAt: optimistic } : prev
        );
        try {
            const saved = await markLoginPromptClosed();
            queryClient.setQueryData<ShopUserInfo | null>(['user'], (prev) =>
                prev ? { ...prev, lastLoginPromptClosedAt: saved.lastLoginPromptClosedAt } : prev
            );
        } catch (err) {
            console.error('Failed to store login prompt pause', err);
        }
    }, [queryClient]);

    const reportWaiting = useCallback((id: LoginPromptId, waiting: boolean) => {
        if (waitingRef.current[id] === waiting) return;
        waitingRef.current = { ...waitingRef.current, [id]: waiting };
        setWaitingVersion((version) => version + 1);
    }, []);

    const claim = useCallback((id: LoginPromptId) => {
        if (grantedIdRef.current !== id) return;
        setClaimed(true);
    }, []);

    const releaseClaim = useCallback(() => {
        setClaimed(false);
    }, []);

    const settle = useCallback(() => {
        const granted = grantedIdRef.current;
        if (granted && interruptRef.current === granted) {
            interruptRef.current = null;
            setInterruptId(null);
        }
        setGrantedId(null);
        setClaimed(false);
        void stamp();
    }, [stamp]);

    const stampClosed = useCallback(() => {
        void stamp();
    }, [stamp]);

    const holdOverlay = useCallback((token: string) => {
        if (holdsRef.current.has(token)) return;
        holdsRef.current.add(token);
        setHoldCount(holdsRef.current.size);
    }, []);

    const releaseOverlay = useCallback((token: string) => {
        if (!holdsRef.current.delete(token)) return;
        setHoldCount(holdsRef.current.size);
    }, []);

    const requestInterrupt = useCallback((id: LoginPromptId) => {
        interruptRef.current = id;
        setInterruptId(id);
    }, []);

    const getGrantedId = useCallback(() => grantedIdRef.current, []);

    const gapMinutes = queueQuery.data?.gapMinutes ?? 3;
    const lastClosedAtMs = user?.lastLoginPromptClosedAt ? Date.parse(user.lastLoginPromptClosedAt) : null;
    const blocked =
        !queueQuery.isFetched ||
        !cookieReady ||
        isCookieConsentPending() ||
        isLoginPromptQuietPath(pathname) ||
        holdCount > 0;

    useEffect(() => {
        if (!isAuthReady || !isAuthenticated) {
            setGrantedId(null);
            setClaimed(false);
            return;
        }

        // Not on screen yet, and either the screen is busy or this prompt is no longer waiting.
        const lostEligibility =
            grantedId != null && interruptId !== grantedId && !waitingRef.current[grantedId];
        if (grantedId && !claimed && (blocked || lostEligibility)) {
            setGrantedId(null);
            return;
        }

        // Deep link waits until the current prompt is finished, then opens without stacking and without the gap.
        if (!grantedId && interruptId && !blocked) {
            setGrantedId(interruptId);
            return;
        }

        const decision = pickLoginPrompt({
            nowMs: Date.now(),
            gapMinutes,
            lastClosedAtMs: lastClosedAtMs != null && Number.isFinite(lastClosedAtMs) ? lastClosedAtMs : null,
            slotOpen: grantedId != null,
            blocked,
            prompts: ordered,
            waitingIds,
        });

        if (decision.kind === 'grant' && isLoginPromptId(decision.id)) {
            if (grantedId !== decision.id) setGrantedId(decision.id);
            return;
        }

        if (decision.kind === 'wait') {
            const delay = Math.max(250, decision.resumeAtMs - Date.now());
            const timer = window.setTimeout(() => setTick((value) => value + 1), delay);
            return () => window.clearTimeout(timer);
        }
    }, [
        isAuthReady,
        isAuthenticated,
        grantedId,
        claimed,
        interruptId,
        blocked,
        gapMinutes,
        lastClosedAtMs,
        ordered,
        waitingIds,
        tick,
    ]);

    // Hide a grant that has not opened yet while the screen is busy, so the prompt cannot paint over it.
    const exposedGrant = grantedId != null && !claimed && blocked ? null : grantedId;

    const value = useMemo<LoginPromptApi>(
        () => ({
            grantedId: exposedGrant,
            shopOverlayHeld: holdCount > 0,
            reportWaiting,
            claim,
            releaseClaim,
            settle,
            stampClosed,
            holdOverlay,
            releaseOverlay,
            requestInterrupt,
            getGrantedId,
        }),
        [
            exposedGrant,
            holdCount,
            reportWaiting,
            claim,
            releaseClaim,
            settle,
            stampClosed,
            holdOverlay,
            releaseOverlay,
            requestInterrupt,
            getGrantedId,
        ]
    );

    return <LoginPromptContext.Provider value={value}>{children}</LoginPromptContext.Provider>;
}

/** The only mount for on-login prompts. Each one opens only while the provider has granted it. */
export default function LoginPromptHost() {
    return (
        <>
            <PendingMeasurementPrompt />
            <PendingMyPetitoNudge />
            <PendingRatingPrompt />
        </>
    );
}
